import React, { useState, useEffect, useRef, Suspense } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { Resizable } from "react-resizable";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import {
  Container,
  Grid,
  AppBar,
  Stack,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Skeleton,
  Box,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Toolbar,
  InputBase,
  FormControl,
  Button,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { BiSearchAlt } from "react-icons/bi";
import { TbDragDrop } from "react-icons/tb";
import { BiAddToQueue } from "react-icons/bi";
import { MdMoreVert, MdRemoveRedEye } from "react-icons/md";
import { PlusIcon } from "../common/Icons";
import { MdDriveFileRenameOutline, MdDeleteForever } from "react-icons/md";
import { listLayouts, createLayout, updateLayout, deleteLayout } from "../../../api/layouts";

const ResponsiveGridLayout = WidthProvider(Responsive);

// ----------------------------------------------------------------------

const Search = styled(FormControl)(({ theme }) => ({
  position: "relative",
  fontSize: theme.typography.fontSize,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  display: "flex",
  width: "100%",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

// ----------------------------------------------------------------------

const initialLayouts = [
  {
    id: 1,
    name: "layout1",
    config: JSON.stringify([
      { w: 4, h: 11, x: 0, y: 6, i: "Tile2" },
      { w: 10, h: 6, x: 2, y: 0, i: "Tile1" },
      { w: 12, h: 12, x: 0, y: 17, i: "Tile6" },
      { w: 2, h: 6, x: 0, y: 0, i: "Tile3" },
      { w: 8, h: 11, x: 4, y: 6, i: "Tile7" },
    ]),
  },
  {
    id: 2,
    name: "layout2",
    config: JSON.stringify([
      { w: 6, h: 8, x: 0, y: 0, i: "Tile8" },
      { w: 6, h: 8, x: 6, y: 0, i: "Tile9" },
      { w: 4, h: 12, x: 0, y: 8, i: "Tile10" },
      { w: 4, h: 12, x: 4, y: 8, i: "Tile11" },
      { w: 4, h: 12, x: 8, y: 8, i: "Tile12" },
    ]),
  },
];

const Placeholder = () => (
  <Card
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <CardHeader
      avatar={<Skeleton variant="circular" width={40} height={40} />}
      action={
        <IconButton disabled>
          <MdMoreVert />
        </IconButton>
      }
      title={<Skeleton variant="text" width="80%" />}
      subheader={<Skeleton variant="text" width="40%" />}
      sx={{ pb: 0 }}
    />
    <CardContent sx={{ flexGrow: 1, pt: 1 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height="100%"
        animation="wave"
      />
    </CardContent>
  </Card>
);

export default function Tiles() {
  const [layouts, setLayouts] = useState(initialLayouts);
  const [selectedLayoutIndex, setSelectedLayoutIndex] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [currentLayout, setCurrentLayout] = useState(
    JSON.parse(initialLayouts[0].config)
  );
  const [componentsMap, setComponentsMap] = useState({});

  const [open, setOpen] = useState(false); // add dialog
  const [editOpen, setEditOpen] = useState(false); // edit dialog
  const [editingRow, setEditingRow] = useState(null);

  const [value, setValue] = useState(0); // tabs
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const saveTimer = useRef(null);

  const handleChange = (_, newValue) => setValue(newValue);
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSearch = (event) => setSearchKey(event.target.value);

  // fetch from backend (list or search)
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await listLayouts({
          page: page + 1,
          pageSize: rowsPerPage,
          q: searchKey,
        });
        const rows = Array.isArray(data) ? data : data.rows || [];
        if (rows.length) {
          setLayouts(rows);
          setSelectedLayoutIndex((prev) => Math.min(prev, rows.length - 1));
        } else {
          setLayouts([]);
          setSelectedLayoutIndex(0);
        }
        setError("");
      } catch (e) {
        // fall back to initial layouts if API fails
        setLayouts((prev) => (prev?.length ? prev : initialLayouts));
        setError(e.message || "Failed to load layouts");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchKey]);

  // when selection or layouts change, rebuild preview + lazy imports
  useEffect(() => {
    if (!layouts.length) {
      setCurrentLayout([]);
      setComponentsMap({});
      return;
    }
    const raw = layouts[selectedLayoutIndex]?.config || "[]";
    let parsedLayout = [];
    try {
      parsedLayout = JSON.parse(raw);
    } catch {
      parsedLayout = [];
    }
    setCurrentLayout(parsedLayout);

    const map = {};
    parsedLayout.forEach(({ i }) => {
      map[i] = React.lazy(() =>
        import(`../sections/Dashboard/${i}`)
          .then((mod) => ({ default: mod.default }))
          .catch(() => ({ default: Placeholder }))
      );
    });
    setComponentsMap(map);
  }, [selectedLayoutIndex, layouts]);

  const handleLayoutChange = (newLayout) => {
    if (!layouts.length) return;

    const updatedLayouts = [...layouts];
    updatedLayouts[selectedLayoutIndex] = {
      ...updatedLayouts[selectedLayoutIndex],
      config: JSON.stringify(newLayout),
    };
    setLayouts(updatedLayouts);
    setCurrentLayout(newLayout);

    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const row = updatedLayouts[selectedLayoutIndex];
        await updateLayout({ id: row.id, name: row.name, config: row.config });
      } catch (e) {
        console.warn("Auto-save failed:", e);
      }
    }, 500);
  };

  const renderItem = (item) => {
    const TileComponent = componentsMap[item.i] || Placeholder;
    const commonProps = {
      className: "resizable",
      width: item.w * 100,
      height: item.h * 30,
      minConstraints: [1, 1],
      maxConstraints: [12, 12],
      onResize: (e, { size }) => {
        const updated = currentLayout.map((l) =>
          l.i === item.i
            ? { ...l, w: size.width / 100, h: size.height / 30 }
            : l
        );
        handleLayoutChange(updated);
      },
    };

    const content = (
      <Suspense
        fallback={
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
          />
        }
      >
        <TileComponent />
      </Suspense>
    );

    return (
      <div key={item.i} data-grid={item}>
        {disabled ? (
          <Box sx={{ width: "100%", height: "100%" }}>{content}</Box>
        ) : (
          <Resizable {...commonProps}>
            <Box sx={{ width: "100%", height: "100%" }}>{content}</Box>
          </Resizable>
        )}
      </div>
    );
  };

  return (
    <>
      {/* ADD DIALOG */}
      {open && (
        <Dialog fullWidth open={open} onClose={handleClose}>
          <DialogTitle>New Layout</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To add a new layout, enter name and configuration (JSON array of grid items).
            </DialogContentText>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Layout Name"
                  margin="dense"
                  name="layoutName"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Configuration (JSON)"
                  margin="dense"
                  name="layoutConfig"
                  multiline
                  minRows={4}
                  placeholder='[{"w":6,"h":8,"x":0,"y":0,"i":"Tile1"}]'
                  defaultValue="[]"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">Cancel</Button>
            <Button
              color="primary"
              onClick={async () => {
                const nameEl = document.querySelector('input[name="layoutName"]');
                const cfgEl =
                  document.querySelector('textarea[name="layoutConfig"]') ||
                  document.querySelector('input[name="layoutConfig"]');

                const layoutName = (nameEl?.value || "").trim();
                const layoutConfigRaw = cfgEl?.value || "[]";
                if (!layoutName) return;

                if (layouts.some(l => l.name?.toLowerCase() === layoutName.toLowerCase())) {
                  alert("A layout with this name already exists.");
                  return;
                }

                let parsed = [];
                try {
                  parsed = JSON.parse(layoutConfigRaw || "[]");
                  if (!Array.isArray(parsed)) throw new Error("Config must be an array");
                } catch {
                  alert("Invalid JSON for configuration");
                  return;
                }

                try {
                  const created = await createLayout({
                    name: layoutName,
                    config: JSON.stringify(parsed),
                  });
                  setLayouts((prev) => [created, ...(prev || [])]);
                  setSelectedLayoutIndex(0);
                  setValue(1); // Preview
                  handleClose();
                } catch (e) {
                  alert(e.message || "Failed to create layout");
                }
              }}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* EDIT DIALOG */}
      {editOpen && editingRow && (
        <Dialog fullWidth open={editOpen} onClose={() => setEditOpen(false)}>
          <DialogTitle>Edit Layout</DialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Layout Name"
                  margin="dense"
                  defaultValue={editingRow.name}
                  name="editName"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Configuration (JSON)"
                  margin="dense"
                  name="editConfig"
                  multiline
                  minRows={6}
                  defaultValue={editingRow.config || "[]"}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                const nameEl = document.querySelector('input[name="editName"]');
                const cfgEl =
                  document.querySelector('textarea[name="editConfig"]') ||
                  document.querySelector('input[name="editConfig"]');

                const newName = (nameEl?.value || "").trim();
                const newConfigRaw = cfgEl?.value || "[]";
                if (!newName) return;

                if (layouts.some(l =>
                  l.id !== editingRow.id &&
                  l.name?.toLowerCase() === newName.toLowerCase()
                )) {
                  alert("Another layout already uses this name.");
                  return;
                }

                let parsed = [];
                try {
                  parsed = JSON.parse(newConfigRaw || "[]");
                  if (!Array.isArray(parsed)) throw new Error();
                } catch {
                  alert("Invalid JSON for configuration");
                  return;
                }

                try {
                  const updated = await updateLayout({
                    id: editingRow.id,
                    name: newName,
                    config: JSON.stringify(parsed),
                  });
                  setLayouts((prev) =>
                    prev.map((r) => (r.id === updated.id ? updated : r))
                  );
                  const idx = layouts.findIndex((l) => l.id === editingRow.id);
                  if (idx === selectedLayoutIndex) {
                    setCurrentLayout(parsed);
                  }
                  setEditOpen(false);
                } catch (e) {
                  alert(e.message || "Update failed");
                }
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Container maxWidth="xl">
        <AppBar
          component="div"
          sx={{ backgroundColor: "transparent", color: "ButtonText" }}
          position="static"
          elevation={0}
        >
          <Toolbar>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs>
                <Typography color="inherit" variant="h6" component="h2">
                  Layouts
                </Typography>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>

        <AppBar
          sx={{ backgroundColor: "transparent", color: "ButtonText" }}
          component="div"
          position="static"
          elevation={0}
        >
          <Tabs value={value} onChange={(_, v) => setValue(v)}>
            <Tab disableRipple label="Overview" />
            <Tab disableRipple label="Preview" />
          </Tabs>
        </AppBar>

        {value === 0 ? (
          <React.Fragment>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AppBar
                  sx={{ bgcolor: "rgba(0, 0, 0, 0)", mt: 7, mb: 3 }}
                  position="static"
                  color="inherit"
                  elevation={0}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs>
                      <Search>
                        <SearchIconWrapper>
                          <BiSearchAlt />
                        </SearchIconWrapper>
                        <StyledInputBase
                          placeholder="Search anything"
                          value={searchKey}
                          onChange={handleSearch}
                          inputProps={{ "aria-label": "search" }}
                        />
                      </Search>
                    </Grid>
                    <Grid item>
                      <Button
                        onClick={handleClickOpen}
                        variant="contained"
                        color="primary"
                      >
                        <PlusIcon /> Layout
                      </Button>
                    </Grid>
                  </Grid>
                </AppBar>
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ p: 2 }}>
              <Table size="small" aria-label="users table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right" sx={{ pr: 5 }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(loading ? Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={`sk-${idx}`}>
                      <TableCell><Skeleton width={40} /></TableCell>
                      <TableCell><Skeleton width={120} /></TableCell>
                      <TableCell><Skeleton width={200} /></TableCell>
                      <TableCell align="right"><Skeleton width={120} /></TableCell>
                    </TableRow>
                  )) : layouts).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedLayoutIndex(
                              layouts.findIndex((l) => l.id === row.id)
                            );
                            setValue(1);
                          }}
                        >
                          <MdRemoveRedEye color="warning" />
                        </IconButton>

                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => {
                            setEditingRow(row);
                            setEditOpen(true);
                          }}
                        >
                          <MdDriveFileRenameOutline />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="success"
                          onClick={async () => {
                            if (!window.confirm(`Delete layout "${row.name}"?`)) return;
                            try {
                              await deleteLayout(row.id);
                              setLayouts((prev) => prev.filter((r) => r.id !== row.id));
                              setSelectedLayoutIndex(0);
                            } catch (e) {
                              alert(e.message || "Delete failed");
                            }
                          }}
                        >
                          <MdDeleteForever />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && !layouts.length && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        {error ? `Error: ${error}` : "No layouts"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20, 30]}
                component="div"
                count={layouts.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Grid container>
              <Grid item sm={12}>
                <AppBar
                  sx={{ mt: 5, mb: 3, pr: 1 }}
                  position="static"
                  color="inherit"
                  elevation={0}
                >
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Select
                          value={selectedLayoutIndex}
                          onChange={(e) => setSelectedLayoutIndex(e.target.value)}
                          displayEmpty
                          sx={{
                            minWidth: 150,
                            border: "1px solid transparent",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent",
                            },
                          }}
                        >
                          {layouts.map((layout, idx) => (
                            <MenuItem key={`${layout.id}-${layout.name}`} value={idx}>
                              {layout.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Stack>
                    </Grid>
                    <Grid item>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Add widget">
                          <IconButton
                            onClick={() => {
                              setCurrentLayout((prev) => {
                                const next = Array.isArray(prev) ? [...prev] : [];
                                next.push({
                                  w: 6,
                                  h: 8,
                                  x: 0,
                                  y: 0,
                                  i: `Tile${Math.floor(Math.random() * 100000)}`,
                                });
                                handleLayoutChange(next);
                                return next;
                              });
                            }}
                            color="primary"
                          >
                            <BiAddToQueue />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={disabled ? "Enable layout resize" : "Disable layout resize"}
                        >
                          <IconButton
                            onClick={() => setDisabled(!disabled)}
                            color={!disabled ? "info" : "neutral"}
                          >
                            <TbDragDrop />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                </AppBar>
              </Grid>
            </Grid>

            <ResponsiveGridLayout
              className="layout"
              layouts={{ lg: currentLayout }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              rowHeight={30}
              isDraggable={!disabled}
              isResizable={!disabled}
              onLayoutChange={handleLayoutChange}
            >
              {currentLayout.map((item) => renderItem(item))}
            </ResponsiveGridLayout>
          </React.Fragment>
        )}
      </Container>
    </>
  );
}
